"use client"

import { useEffect, useState } from "react"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import { motion, AnimatePresence } from "framer-motion"

const SuccessModal = () => {
  const { isSuccessModalOpen, message, hideModal } = useSuccessFailure()
  const [show, setShow] = useState(false)
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    if (isSuccessModalOpen) {
      setShow(true)
      setProgress(100)

      const progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev <= 0) {
            clearInterval(progressTimer)
            return 0
          }
          return prev - 100 / 30
        })
      }, 100)

      const timer = setTimeout(() => {
        setShow(false)
        setTimeout(() => hideModal(), 500)
      }, 3000)

      return () => {
        clearTimeout(timer)
        clearInterval(progressTimer)
      }
    }
  }, [isSuccessModalOpen, hideModal])

  const handleClose = () => {
    setShow(false)
    setTimeout(() => hideModal(), 500)
  }

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.7, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 50 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
              duration: 0.4,
            }}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md mx-4 sm:mx-auto sm:w-auto sm:max-w-md sm:min-w-[320px]"
          >
            <div className="relative bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/30 overflow-hidden">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-green-500/10 pointer-events-none" />

              {/* Progress bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-100/50 to-green-100/50 dark:from-emerald-900/50 dark:to-green-900/50">
                <motion.div
                  className="h-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-green-500 shadow-lg shadow-emerald-500/30"
                  initial={{ width: "100%" }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.1, ease: "linear" }}
                />
              </div>

              <div className="relative p-8">
                {/* Success Icon */}
                <div className="flex justify-center mb-6">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      delay: 0.2,
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                    }}
                    className="relative"
                  >
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/40">
                      {/* Animated rings */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0] }}
                        transition={{
                          delay: 0.5,
                          duration: 2,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatDelay: 1,
                        }}
                        className="absolute inset-0 rounded-full border-4 border-emerald-400"
                      />
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0] }}
                        transition={{
                          delay: 0.7,
                          duration: 2,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatDelay: 1,
                        }}
                        className="absolute inset-0 rounded-full border-2 border-green-300"
                      />

                      {/* Checkmark */}
                      <motion.svg
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{
                          delay: 0.6,
                          duration: 0.8,
                          ease: "easeInOut",
                        }}
                        className="w-10 h-10 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={3}
                      >
                        <motion.path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </motion.svg>
                    </div>
                  </motion.div>
                </div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="text-center"
                >
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-3">
                    Success!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed mb-6">{message}</p>
                </motion.div>

                {/* Action buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="flex justify-center space-x-3"
                >
                  <button
                    onClick={handleClose}
                    className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-105 transition-all duration-200 ease-out"
                  >
                    Continue
                  </button>
                </motion.div>

                {/* Close button */}
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                  onClick={handleClose}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 flex items-center justify-center transition-all duration-200 group shadow-lg"
                  aria-label="Close success message"
                >
                  <svg
                    className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>

                {/* Decorative elements */}
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-emerald-400/20 to-green-600/20 rounded-full blur-2xl" />
                <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-gradient-to-tr from-green-400/20 to-emerald-600/20 rounded-full blur-xl" />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default SuccessModal
